import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import cogoToast from "cogo-toast-react-17-fix";
import API from "../../../ApiConfig";
import styles from "./Consent.module.css";
import OtpInputs from "../../../Utils/OtpInputs";
import usePageTitle from "../../../Hooks/useDocTitle";
import { Timer, resetTimer, stringFormatter } from "../../../Utils/helper";
import { APP_NAME, CONSENT } from "../../../Utils/Strings";
import {
  GET_CONSENT_DETAILS,
  SEND_CONSENT_OTP,
  SUBMIT_HOLDER_CONSENT,
  VERIFY_CONSENT_OTP
} from "../../../Utils/constant";
import {
  buildConsentReceipt,
  isDeadLinkError,
  normalizeConsent,
  normalizeOtpInfo,
  readApiMessage
} from "./consentAdapter";
import { EntryGrid, NoticeSection, displayValue } from "./NoticeSections";

const SCREEN = { LOADING: "loading", REVIEW: "review", OTP: "otp", DONE: "done", DEAD: "dead" };
const ACTION = { APPROVE: "approve", REJECT: "reject" };

const Brand = () => (
  <header className={styles.topBar}>
    {/* _1 is the navy logo; /authnumber.svg is the white one, for dark panels. */}
    <img className={styles.subtractIcon} loading="eager" alt="" src="/Header_icon.svg" />
    <img className={styles.brandLogo} loading="eager" src="/authnumber_1.svg" alt={APP_NAME} />
  </header>
);

const PageFooter = () => (
  <footer className={styles.pageFooter}>
    <span>{CONSENT.SECURED_BY}</span>
    <strong>{APP_NAME}</strong>
  </footer>
);

const Consent = () => {
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token")
  usePageTitle(CONSENT.PAGE_TITLE);

  const [screen, setScreen] = useState(SCREEN.LOADING);
  const [consent, setConsent] = useState(null);
  const [action, setAction] = useState(null);
  const [purposeConsents, setPurposeConsents] = useState({});
  const [ackConsents, setAckConsents] = useState({});
  /* Only used by notices that carry neither purposes nor acknowledgements. */
  const [agreed, setAgreed] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpInfo, setOtpInfo] = useState(null);
  const [otpKey, setOtpKey] = useState(0);
  const [resetOtp, setResetOtp] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null); // approved | rejected | already
  const [deadReason, setDeadReason] = useState(null); // expired | invalid | error
  const [errorMessage, setErrorMessage] = useState(null);

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const fetchConsent = useCallback(() => {
    setScreen(SCREEN.LOADING);
    setErrorMessage(null);
    API.post(GET_CONSENT_DETAILS, { token })
      .then((res) => {
        if (!isMounted.current) return;
        const data = normalizeConsent(res.data);
        setConsent(data);
        /* A fresh payload means fresh consents; the adapter's keys are derived
           from the notice, so stale ticks must not carry over. */
        setPurposeConsents({});
        setAckConsents({});
        setAgreed(false);
        if (data.gate === "dead") {
          setDeadReason(data.gateReason);
          setScreen(SCREEN.DEAD);
        } else if (data.gate === "done") {
          setOutcome("already");
          setScreen(SCREEN.DONE);
        } else {
          setScreen(SCREEN.REVIEW);
        }
      })
      .catch((err) => {
        if (!isMounted.current || err?.name === "CanceledError") return;
        setDeadReason(isDeadLinkError(err) ? "invalid" : "error");
        setErrorMessage(readApiMessage(err, CONSENT.INVALID_BODY));
        setScreen(SCREEN.DEAD);
      });
  }, [token]);

  useEffect(() => {
    if (!token) {
      setDeadReason("invalid");
      setScreen(SCREEN.DEAD);
      return;
    }
    fetchConsent();
  }, [token, fetchConsent]);

  useEffect(() => resetTimer, []);

  const sendOtp = (nextAction, isResend = false) => {
    setSending(true);
    API.post(SEND_CONSENT_OTP, { token })
      .then((res) => {
        if (!isMounted.current) return;
        setOtpInfo(normalizeOtpInfo(res.data));
        setAction(nextAction);
        setOtp("");
        setResetOtp((prev) => !prev);
        setOtpKey(Date.now());
        setScreen(SCREEN.OTP);
        cogoToast.success(CONSENT.OTP_SENT);
      })
      .catch((err) => {
        if (!isMounted.current || err?.name === "CanceledError") return;
        if (isDeadLinkError(err)) {
          setDeadReason("expired");
          setScreen(SCREEN.DEAD);
        }
        cogoToast.error(readApiMessage(err, CONSENT.OTP_FAILED));
      })
      .finally(() => isMounted.current && setSending(false));
    if (isResend) setOtp("");
  };
  /* Declining never requires a consent tick -- the guard sits inside the
     approve branch by design. */
  const startAction = (nextAction) => {
    if (nextAction === ACTION.APPROVE && !canApprove) {
      cogoToast.warn(needsLegacyAgree ? CONSENT.AGREE_REQUIRED : CONSENT.CONSENT_REQUIRED);
      return;
    }
    resetTimer();
    sendOtp(nextAction);
  };
  const submitConsent=()=>{
    setSubmitting(true)
    const accepted = action === ACTION.APPROVE
    const receipt = buildConsentReceipt(consent, purposeConsents, ackConsents, token, accepted);
    /* Rejections carry no proof of what was (never) consented to. */
    const payload = accepted
      ? receipt
      : { token, accepted: false, content_hash: receipt.content_hash };

    if (process.env.NODE_ENV === "development") console.debug("[consent] receipt", payload)
      API.post(SUBMIT_HOLDER_CONSENT,payload)
    .then((res)=>{
      if (!isMounted.current) return
      resetTimer();
      setOutcome(action === ACTION.APPROVE ? "approved" : "rejected");
      setScreen(SCREEN.DONE)
  
})
.catch((err) => {
  if (!isMounted.current || err?.name === "CanceledError") return;
  if (isDeadLinkError(err)) {
    setDeadReason("expired");
    setScreen(SCREEN.DEAD);
    return;
  }
  setOtp("");
  setResetOtp((prev) => !prev);
  cogoToast.error(readApiMessage(err, "Something Went Wrong in Submittimg Details!!"))
  })
  .finally(()=>{isMounted.current && setSubmitting(false)})
}
  const verifyConsent = (event) => {
    event.preventDefault();
    if (!otp || otp.length !== 6) {
      cogoToast.warn(CONSENT.OTP_REQUIRED);
      return;
    }
    setSubmitting(true);
    API.post(VERIFY_CONSENT_OTP, {
      token,
      otp
    })
      .then((res) => {
        if(res.data.data.otp_verified){
          submitConsent()
        }
      })
      .catch((err) => {
        if (!isMounted.current || err?.name === "CanceledError") return;
        if (isDeadLinkError(err)) {
          setDeadReason("expired");
          setScreen(SCREEN.DEAD);
          return;
        }
        setOtp("");
        setResetOtp((prev) => !prev);
        cogoToast.error(readApiMessage(err, "Incorrect OTP. Please check again or resend OTP"));
      })
      .finally(() => isMounted.current && setSubmitting(false));
  };

  const requesterName = consent?.requester?.name;
  const heading = useMemo(
    () =>
      requesterName
        ? stringFormatter(CONSENT.SUB_HEADING, [requesterName])
        : CONSENT.SUB_HEADING_FALLBACK,
    [requesterName]
  );

  const otpDestination = otpInfo?.destination;

  const holderEntries = useMemo(
    () =>
      [{ label: CONSENT.FIELD.NAME, value: consent?.holder?.name }].filter((entry) => entry.value),
    [consent]
  );

  const togglePurpose = useCallback(
    (key, on) => setPurposeConsents((prev) => ({ ...prev, [key]: on })),
    []
  );
  const toggleAck = useCallback(
    (key, on) => setAckConsents((prev) => ({ ...prev, [key]: on })),
    []
  );
  const sectionCtx = useMemo(
    () => ({ purposeConsents, ackConsents, togglePurpose, toggleAck }),
    [purposeConsents, ackConsents, togglePurpose, toggleAck]
  );

  const missingCount = useMemo(() => {
    if (!consent) return 0;
    const purposes = consent.purposes.filter((p) => p.mandatory && !purposeConsents[p.key]).length;
    const acks = consent.acknowledgements.filter((a) => a.mandatory && !ackConsents[a.key]).length;
    return purposes + acks;
  }, [consent, purposeConsents, ackConsents]);

  /* A notice with no per-purpose consents and no acknowledgements still has to
     gate on something, so it falls back to the single master checkbox. */
  const needsLegacyAgree = Boolean(
    consent && !consent.purposes.length && !consent.acknowledgements.length
  );
  const canApprove = missingCount === 0 && (!needsLegacyAgree || agreed);

  const renderLoading = () => (
    <section className={styles.card} aria-busy="true">
      <div className={styles.centered}>
        <div className={styles.spinner} aria-hidden="true" />
        <p className={styles.lead}>{CONSENT.LOADING}</p>
      </div>
      <div className={styles.skeletonBlock}>
        <span className={`shimmer ${styles.skeletonLine}`} />
        <span className={`shimmer ${styles.skeletonLine}`} />
        <span className={`shimmer ${styles.skeletonLineShort}`} />
      </div>
    </section>
  );

  const renderReview = () => (
    <section className={styles.card}>
      <div className={styles.hero}>
        <div className={styles.heroText}>
          <h1 className={styles.title}>{CONSENT.HEADING}</h1>
          <p className={styles.lead}>{heading}</p>
        </div>
      </div>

      {consent.meta.length > 0 && (
        <ul className={styles.metaRow}>
          {consent.meta.map((item) => (
            <li className={styles.metaChip} key={item.label}>
              <span className={styles.metaLabel}>{item.label}</span>
              <span className={styles.metaValue}>{displayValue(item.value)}</span>
            </li>
          ))}
        </ul>
      )}

      {holderEntries.length > 0 && (
        <div className={styles.block}>
          <h2 className={styles.blockTitle}>{CONSENT.REQUESTED_FROM}</h2>
          <EntryGrid entries={holderEntries} />
        </div>
      )}

      {requesterName && (
        <div className={styles.block}>
          <h2 className={styles.blockTitle}>{CONSENT.REQUESTED_BY}</h2>
          <p className={styles.blockText}>{requesterName}</p>
        </div>
      )}

      {consent.checks.items.length > 0 && (
        <div className={styles.block}>
          <h2 className={styles.blockTitle}>{CONSENT.CHECKS_HEADING}</h2>
          <ul className={styles.tagList}>
            {consent.checks.items.map((check) => (
              <li className={styles.tag} key={check}>{check}</li>
            ))}
          </ul>
        </div>
      )}

      {consent.notice ? (
        <div className={styles.block}>
          {/* The notice's own title is skipped when it just repeats the page heading. */}
          {consent.notice.title && consent.notice.title !== CONSENT.HEADING && (
            <h2 className={styles.blockTitle}>{consent.notice.title}</h2>
          )}
          {consent.notice.sections.map((section) => (
            <NoticeSection section={section} ctx={sectionCtx} key={section.key} />
          ))}
          {(consent.notice.footer.versionLabel || consent.notice.footer.lastUpdated) && (
            <p className={styles.footerNote}>
              {stringFormatter(CONSENT.NOTICE_VERSION, [
                consent.notice.footer.versionLabel || consent.notice.version,
                consent.notice.footer.lastUpdated || "—"
              ])}
            </p>
          )}
        </div>
      ) : (
        /* A missing notice is not a dead link -- the request details still
           render, and the master checkbox below takes over the gating. */
        <div className={styles.block}>
          <p className={styles.blockText}>{CONSENT.NOTICE_UNAVAILABLE}</p>
        </div>
      )}

      {needsLegacyAgree && (
        <div className={styles.block}>
          <h2 className={styles.blockTitle}>{CONSENT.DECLARATION}</h2>
          <ul className={styles.declarationList}>
            <li>{CONSENT.DEFAULT_DECLARATION}</li>
          </ul>
          <label className={styles.agreeRow}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
            />
            <span>{CONSENT.AGREE_LABEL}</span>
          </label>
        </div>
      )}

      {missingCount > 0 && (
        <p className={styles.gateNotice} aria-live="polite">
          {stringFormatter(CONSENT.CONSENT_PENDING_HINT, [missingCount])}
        </p>
      )}

      <div className={styles.actionBar}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnGhost}`}
          onClick={() => startAction(ACTION.REJECT)}
          disabled={sending}
        >
          {CONSENT.REJECT}
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => startAction(ACTION.APPROVE)}
          disabled={sending || !canApprove}
        >
          {sending ? CONSENT.VERIFYING : CONSENT.APPROVE}
        </button>
      </div>
    </section>
  );

  const renderOtp = () => (
    <section className={styles.card}>
      <button
        type="button"
        className={styles.backLink}
        onClick={() => {
          resetTimer();
          setScreen(SCREEN.REVIEW);
        }}
      >
        ← {CONSENT.BACK}
      </button>

      <h1 className={styles.title}>{CONSENT.OTP_HEADING}</h1>
      <p className={styles.lead}>
        {otpDestination
          ? stringFormatter(CONSENT.OTP_SUB_HEADING, [otpDestination])
          : CONSENT.OTP_SUB_HEADING_FALLBACK}
      </p>

      <div className={styles.otpSummary}>
        <span className={action === ACTION.APPROVE ? styles.pillApprove : styles.pillReject}>
          {action === ACTION.APPROVE ? CONSENT.APPROVE : CONSENT.REJECT}
        </span>
      </div>

      <form className={styles.otpForm} onSubmit={verifyConsent}>
        <div className={styles.otpInputs}>
          <OtpInputs getOtp={(value) => setOtp(value || "")} resetTrigger={resetOtp} />
        </div>

        <div className={styles.timerRow}>
          <Timer m={otpInfo?.expiresIn || 0.5} getOTP={() => sendOtp(action, true)} key={otpKey} />
        </div>

        <div className={styles.actionBar}>
          <button
            type="submit"
            className={`${styles.btn} ${action === ACTION.APPROVE ? styles.btnPrimary : styles.btnDanger}`}
            disabled={submitting || sending || otp.length !== 6}
          >
            {submitting
              ? CONSENT.VERIFYING
              : action === ACTION.APPROVE
                ? CONSENT.CONFIRM_APPROVE
                : CONSENT.CONFIRM_REJECT}
          </button>
        </div>
      </form>
    </section>
  );

  const renderDone = () => {
    const isApproved = outcome === "approved";
    const copy =
      outcome === "already"
        ? { title: CONSENT.ALREADY_DONE_TITLE, body: CONSENT.ALREADY_DONE_BODY }
        : isApproved
          ? { title: CONSENT.APPROVED_TITLE, body: CONSENT.APPROVED_BODY }
          : { title: CONSENT.REJECTED_TITLE, body: CONSENT.REJECTED_BODY };

    return (
      <section className={`${styles.card} ${styles.statusCard}`} aria-live="polite">
        <img
          className={styles.statusIcon}
          src={outcome === "rejected" ? "/cancel.svg" : "/checkcircle.svg"}
          alt=""
        />
        <h1 className={styles.title}>{copy.title}</h1>
        <p className={styles.lead}>{copy.body}</p>
      </section>
    );
  };

  const renderDead = () => {
    const copy =
      deadReason === "expired"
        ? { title: CONSENT.EXPIRED_TITLE, body: CONSENT.EXPIRED_BODY }
        : { title: CONSENT.INVALID_TITLE, body: errorMessage || CONSENT.INVALID_BODY };

    return (
      <section className={`${styles.card} ${styles.statusCard}`} aria-live="polite">
        <img className={styles.statusIcon} src="/cancel.svg" alt="" />
        <h1 className={styles.title}>{copy.title}</h1>
        <p className={styles.lead}>{copy.body}</p>
        {deadReason === "error" && token && (
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={fetchConsent}>
            {CONSENT.RETRY}
          </button>
        )}
      </section>
    );
  };

  return (
    <div className={styles.page}>
      <Brand />
      <main className={styles.shell}>
        {screen === SCREEN.LOADING && renderLoading()}
        {screen === SCREEN.REVIEW && consent && renderReview()}
        {screen === SCREEN.OTP && renderOtp()}
        {screen === SCREEN.DONE && renderDone()}
        {screen === SCREEN.DEAD && renderDead()}
      </main>
      <PageFooter />
    </div>
  );
};

export default Consent;
